// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/** A hole's scorecard row: what the course was configured with, not what the map shows. */
export type CourseHole = {
  number: number;
  par: number | null;
  /** As entered on the course-config screen. Null when nobody typed one in. */
  lengthMeters: number | null;
  /**
   * The same hole measured from each individual tee. Empty on a course where nobody has
   * entered per-tee lengths, and on a server that predates them.
   */
  teeLengths: CourseHoleTeeLength[];
};

/** One hole's length from one named tee. */
export type CourseHoleTeeLength = {
  teeId: string;
  teeName: string;
  /** "male" | "female" | "unisex" — two tees can share a name and differ only in this. */
  sex: string;
  lengthMeters: number;
};

type HoleResponse = Record<string, unknown>;

const num = (row: HoleResponse, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const str = (row: HoleResponse, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const mapTeeLength = (row: HoleResponse): CourseHoleTeeLength | null => {
  const teeId = str(row, 'teeId', 'TeeId');
  const lengthMeters = num(row, 'lengthMeters', 'LengthMeters');
  if (!teeId || lengthMeters === null || lengthMeters <= 0) return null;
  return {
    teeId,
    teeName: str(row, 'teeName', 'TeeName'),
    sex: str(row, 'sex', 'Sex'),
    lengthMeters,
  };
};

const mapHole = (row: HoleResponse): CourseHole | null => {
  const number = num(row, 'number', 'Number');
  if (number === null || number <= 0) return null;
  const teeLengths = row.teeLengths ?? row.TeeLengths;
  return {
    number,
    par: num(row, 'par', 'Par'),
    lengthMeters: num(row, 'lengthMeters', 'LengthMeters'),
    teeLengths: Array.isArray(teeLengths)
      ? (teeLengths as HoleResponse[]).map(mapTeeLength).filter((l): l is CourseHoleTeeLength => l !== null)
      : [],
  };
};

/**
 * The course's scorecard holes — par and the entered length, by hole number.
 *
 * Separate from {@link useHoleFeatures}, which reads the geometry: par is not a mapped thing
 * and never arrives with the tees and greens. Empty on any failure, so a caller renders
 * without it rather than showing a wrong par.
 */
export function useCourseHoles(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): CourseHole[] {
  const [holes, setHoles] = useState<CourseHole[]>([]);

  useEffect(() => {
    if (!token || !courseId) {
      setHoles([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!response.ok) {
          setHoles([]);
          return;
        }
        const payload = (await response.json()) as { holes?: HoleResponse[]; Holes?: HoleResponse[] };
        if (cancelled) return;
        const rows = payload.holes ?? payload.Holes ?? [];
        setHoles(Array.isArray(rows) ? rows.map(mapHole).filter((h): h is CourseHole => h !== null) : []);
      } catch {
        if (!cancelled) setHoles([]);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token]);

  return holes;
}
